/**
 * Factory for creating product page data with consistent structure
 */
export default class ProductPageFactory {
  /**
   * Creates product page data structure with all required properties
   * 
   * @param {Object} options - Configuration object with product specific data 
   * @returns {Object} - Complete product data structure
   */
  static createProduct(options) {
    const {
      type, // Product type (e.g., 'Auto', 'Home', 'Life')
      sections = [], // Navigation sections
      introData = {}, // Introduction section data
      typesList = [], // List of insurance types (optional)
      typesListTitle = '', // Title for types list section (optional)
      bestRateTips = [], // Tips for getting the best rate
      briefGuide = '', // Brief product guide text
      coverageItems = [], // Coverage list items
      additionalInfo = {}, // Additional product information
      pricingFactors = {}, // Pricing factors data
      relatedProducts = [], // Related insurance products
      ctaText = '' // Custom text for CTA section (optional)
    } = options;

    // Default sections if none provided
    const defaultSections = [
      { id: `${type.toLowerCase()}1`, title: `${type} Insurance Introduction` },
      { id: 'htgbr', title: 'How to Get The Best Rate' },
      { id: 'coverage', title: 'Components and Coverage' },
      { id: `${type.toLowerCase()}2`, title: 'Main Price Impacting Factors' }
    ];

    // Return complete data structure with defaults for missing sections
    return {
      type,
      sections: sections.length ? sections : defaultSections,
      typesList, // New field for specific product types
      typesListTitle, // New field for types list title
      introData: {
        title: introData.title || `${type} Insurance Guide`,
        paragraphs: introData.paragraphs || [
          `Welcome to our ${type} insurance guide!`,
          `Learn about ${type} insurance options and benefits.`,
          `Contact us to get started with your ${type} insurance policy.`
        ]
      },
      bestRateTips: bestRateTips.length ? bestRateTips : [
        "Shop around to compare rates from different providers.",
        "Bundle multiple policies for discounts.",
        "Consider a higher deductible to lower premiums.",
        "Ask about available discounts.",
        "Maintain a good record (driving, claims history, etc.)."
      ],
      briefGuide: briefGuide || `${type} insurance provides essential protection for your assets.`,
      coverageItems: coverageItems.length ? coverageItems : [
        `What is ${type} insurance and why is it important`,
        `Types of ${type} insurance coverage`,
        `Understanding deductibles and coverage limits`,
        `Common exclusions and limitations`,
        `How to choose the right coverage for your needs`
      ],
      additionalInfo: {
        overview: additionalInfo.overview || `${type} insurance helps protect against financial losses.`,
        sections: additionalInfo.sections || []
      },
      pricingFactors: pricingFactors.title ? pricingFactors : {
        title: `Main Price Impacting Factors for ${type} Insurance`,
        description: `Several factors affect your ${type} insurance premium rates.`,
        factors: []
      },
      relatedProducts: relatedProducts.length ? relatedProducts : [],
      ctaText // New field for custom CTA text
    };
  }
} 